/**
 * SkyPage.js
 *
 * The shared sky page shell: a slowly rotating night sky over layered mountains,
 * with a nav and a dark footer. Everything a page needs to sit on the sky, with
 * a hole in the middle for its own content.
 *
 * Extracted from LandingSky.js on October 3, 2026, ahead of putting /login,
 * /join-waitlist and /forgot-password on the same backdrop. The landing page is
 * now one consumer of this rather than the owner of it.
 *
 * WHAT THIS OWNS
 * --------------
 *   - the page root and its ref, and the --landing-sky-t layout effect
 *   - the sky scale k: state, the star-field useMemo, resize handling
 *   - the <body> override effect
 *   - the self-hosted font imports
 *   - the backdrop: glow, rotating disc, stars, streaks, mountains, conifers,
 *     and (landing page only, via the `ufo` prop) the saucer
 *   - the nav, with the dark lockup linking to "/"
 *   - the footer slot, with the dark MarketingFooter
 *
 * WHAT A PAGE OWNS
 * ----------------
 * Its main content (passed as `children`) and, optionally, whatever sits on the
 * right of the nav (`navRight`). Nothing else. A page should not need to know
 * that k or t exist.
 *
 * Each page mounts its own SkyPage — there is no layout route and no <Outlet>
 * here, deliberately. Navigating between two sky pages therefore remounts the
 * sky and restarts the rotation from its starting angle. That is the existing
 * behaviour and this extraction does not change it; making the sky persist would
 * mean introducing a layout route, which is a pattern this codebase does not use
 * anywhere yet.
 *
 * ARCHITECTURE NOTES
 * ------------------
 * - Standalone. A sky page renders OUTSIDE the authenticated app shell, like
 *   /privacy and /terms, and draws its own nav rather than the shared Header —
 *   the shared Header is a light-background lockup with Login/Join buttons and
 *   would fight the sky. See the routing in App.js.
 * - No canvas, no requestAnimationFrame, no animation library. The star data is
 *   generated once (useMemo) into plain objects, rendered as plain elements, and
 *   every bit of motion is CSS. The rotation is a single transform on one layer.
 * - The star field is synthetic and decorative. It represents no real CKG data.
 */

import React, { useMemo, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// Self-hosted fonts. Only the faces a sky page uses: Fraunces 300 for display
// headings, IBM Plex Sans 400/500/600 for everything else, and IBM Plex Sans 400
// italic for emphasis. @fontsource ships the woff2/woff files inside the package
// and the @font-face src values are relative paths, so webpack bundles them and
// nothing is fetched from a third party. Imported in this component rather than
// in global.css so the rest of the app is untouched.
//
// The italic import is what makes emphasis a real italic rather than a
// browser-synthesized slant: without it the browser has only the upright face to
// work from and shears it.
//
// These live here rather than in each page so that adding a sky page does not
// mean remembering five imports — and so a page that drops its italic text
// cannot silently take the face away from another page that still needs it.
// Fraunces 300 is the landing headline; 400 is the auth cards' heading. Both are
// loaded so neither is browser-synthesized — asking for a weight that is not
// loaded makes the browser shear the one it has, which on a display serif is
// obvious. @fontsource/fraunces ships both; no new package.
import '@fontsource/fraunces/300.css';
import '@fontsource/fraunces/400.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/400-italic.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';

import MarketingFooter from '../common/MarketingFooter/MarketingFooter';
import DarkLockup from './DarkLockup';
import MountainRange from './MountainRange';
import ConiferStand from './ConiferStand';
import SkyUfo, { SHOW_UFO } from './SkyUfo';
import { generateStarField, computeSkyScale, POLE_OFFSET_X, POLE_Y, DISC_R } from './starField';
import { computeHeightFactor } from './heightFactor';
import './SkyPage.css';

// The sky's darkest band. Painted on <body> while a sky page is mounted so iOS
// overscroll (and any gap a mid-scroll repaint leaves) shows the sky's colour
// rather than the white default.
const SKY_DARKEST = '#070B16';

// Resize settle time before the sky is remeasured. Applies to the sky only —
// the compact-layout factor is applied immediately, see the effect below.
const RESIZE_SETTLE_MS = 150;

// The custom property SkyPage.css reads for every height-sensitive value.
const HEIGHT_FACTOR_PROP = '--landing-sky-t';

const readSkyScale = () => computeSkyScale(window.innerWidth, window.innerHeight);
const readHeightFactor = () => computeHeightFactor(window.innerWidth, window.innerHeight);

/**
 * @param {object}    props
 * @param {React.ReactNode} props.children  The page's main content, rendered
 *   between the nav and the footer. The page supplies its own landmark element
 *   (a <main>, typically) and its own classes; SkyPage adds no wrapper, so the
 *   DOM is exactly what the page writes.
 * @param {React.ReactNode} [props.navRight]  Optional node for the right of the
 *   nav. Omitted, the nav renders with the lockup alone on the left and nothing
 *   opposite it — which is what the auth pages want, since offering "Login" on
 *   the login page would be odd.
 * @param {boolean} [props.ufo=false]  Adds the saucer Easter egg (SkyUfo) to the
 *   backdrop. Only the landing page passes it (ruled October 19, 2026); the auth
 *   pages keep the sky they have. Ignored when SHOW_UFO in SkyUfo.js is false.
 */
const SkyPage = ({ children, navRight, ufo = false }) => {
  // -------------------------------------------------------------------------
  // Sky scale (k). The disc has to be big enough to cover the viewport from the
  // pole, or its curved edge shows; see computeSkyScale() in starField.js.
  //
  // Two things keep this from thrashing:
  //
  //   1. k is quantised to 0.05 in computeSkyScale, so most resize events
  //      resolve to the value already in state. The updater returns `previous`
  //      unchanged in that case, and React bails out without re-rendering — so
  //      a slow drag across 1400px of width regenerates the field about nine
  //      times, not once per frame.
  //   2. The listener is debounced, so even crossing a step mid-drag waits for
  //      the window to settle first.
  // -------------------------------------------------------------------------
  const [skyScale, setSkyScale] = useState(readSkyScale);

  const pageRef = useRef(null);

  // -------------------------------------------------------------------------
  // One resize listener, two deliberately different update policies.
  //
  // t (the compact-layout height factor) is written straight to the element as
  // a custom property on EVERY resize event. That is what makes dragging the
  // window edge smooth. It is pointedly NOT React state: t changes continuously
  // between 640px and 760px of height, and routing it through a render would
  // re-render all ~440 star elements for a value no star depends on. Writing it
  // through the ref also means React never manages the property, so a later
  // re-render (from skyScale) cannot clobber it.
  //
  // k regenerates the entire field, so it stays debounced and quantised.
  //
  // useLayoutEffect, not useEffect: this runs after the DOM is mutated but
  // BEFORE the browser paints, so a short window gets the compact layout on its
  // first frame. With useEffect the page would paint once at the default t of 1
  // and then snap, which on a 640px-tall window is a visible jump.
  // -------------------------------------------------------------------------
  useLayoutEffect(() => {
    let timer = null;

    const applyHeightFactor = () => {
      const node = pageRef.current;
      if (node) node.style.setProperty(HEIGHT_FACTOR_PROP, String(readHeightFactor()));
    };

    applyHeightFactor();

    const onResize = () => {
      applyHeightFactor();

      clearTimeout(timer);
      timer = setTimeout(() => {
        setSkyScale((previous) => {
          const next = readSkyScale();
          return next === previous ? previous : next;
        });
      }, RESIZE_SETTLE_MS);
    };

    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Regenerated only when k changes. Deterministic (seeded), so the sky is
  // identical on every load at a given k; see starField.js on why the draw order
  // is load-bearing, and on why a larger k spreads the same stars rather than
  // drawing new ones.
  //
  // The generator also returns `lines`, deliberately not destructured here: the
  // faint lines between stars are not drawn, because nearest-neighbour proximity
  // is not a real relationship between two entries. See SHOW_STAR_LINES in
  // starField.js, which is where they come back if a later phase gives them
  // something true to mean.
  const { stars, ambientBoxShadow, discSize } = useMemo(
    () => generateStarField(skyScale),
    [skyScale]
  );

  // -------------------------------------------------------------------------
  // Page-level <body> overrides.
  //
  // global.css sets `body { color: #333 }` and no background at all. Both are
  // wrong for a full-bleed dark page: the text colour leaks into anything that
  // does not set its own, and the missing background is what makes iOS rubber-
  // band scrolling flash white below the sky.
  //
  // Set on mount, restored on unmount — this is a shared element and the next
  // page is entitled to the styles it was written against. Previous inline
  // values are captured rather than assumed empty, in case something else is
  // already managing them.
  //
  // WHY SKY -> SKY NAVIGATION DOES NOT STRAND THE OVERRIDE (tested, Oct 2026)
  //
  // Every page mounts its own SkyPage, so during a sky-to-sky navigation there
  // are briefly two instances — this effect is NOT a single owner, and an
  // earlier comment here claiming otherwise was wrong.
  //
  // What makes it safe is React's commit ordering: within one commit React
  // flushes the outgoing tree's passive cleanups BEFORE the incoming tree's
  // passive effects. Verified directly — a route swap logs
  // `mount:A -> cleanup:A -> mount:B`. So the incoming SkyPage captures the
  // value the outgoing one has already restored, and a later unmount restores
  // that same original. A sky -> sky -> plain navigation leaves <body> exactly
  // as it was before the first sky mounted, including when the pre-sky values
  // were non-empty.
  //
  // The load-bearing condition is that the two instances are in DIFFERENT
  // commits-worth of tree — i.e. one replaces the other. Two SkyPages rendered
  // as simultaneous siblings would break it: the second would capture the
  // first's override as "previous". Nothing does that today, and nothing
  // should.
  //
  // MOBILE BANNER: the sky starts at the viewport top unconditionally, and no
  // offset needs reserving here. Verified rather than assumed — every rule keyed
  // off `body.has-mobile-banner` is a DESCENDANT selector naming a container
  // a sky page does not use: `.standalone-document` and `.main-container`
  // (global.css:42, :58) and `.app-header` (Header.css:20). A sky page renders
  // `.landing-sky-page` at the top level and draws its own nav, so none of the
  // three can match. That holds even if the class were somehow left on <body> by
  // a previous page — though it is not, since MobileBanner removes it on unmount
  // (MobileBanner.js:39-41).
  // -------------------------------------------------------------------------
  useEffect(() => {
    const { body } = document;
    const previousBackground = body.style.background;
    const previousColor = body.style.color;

    body.style.background = SKY_DARKEST;
    body.style.color = '#E8ECEC';

    return () => {
      body.style.background = previousBackground;
      body.style.color = previousColor;
    };
  }, []);

  // The rotating disc is positioned so its centre lands on the pole. Every value
  // derives from the starField constants and the same k the field was built
  // with, so the pole has exactly one definition and cannot drift out of step
  // with the geometry.
  //
  // Horizontally: `calc(50% + Npx)` rather than a hard 700px. N is the pole's
  // offset from the reference's centre (700 - 640 = 60), scaled by k. Anchoring
  // to the centre rather than the left edge is what keeps the composition
  // centred as the window widens — and computeSkyScale() assumes exactly this
  // placement when it picks k, so the two must stay in agreement.
  //
  // The negative margin then pulls the disc back by its own radius, so that
  // `left` addresses its centre instead of its left edge.
  //
  // Vertically it stays absolute: the pole sits just above the top edge
  // regardless of viewport height.
  const radius = DISC_R * skyScale;
  const discStyle = {
    width: `${discSize}px`,
    height: `${discSize}px`,
    left: `calc(50% + ${POLE_OFFSET_X * skyScale}px)`,
    top: `${POLE_Y * skyScale - radius}px`,
    marginLeft: `-${radius}px`
  };

  return (
    // The ref is how the compact-layout factor reaches the DOM; see the effect
    // above. --landing-sky-t is deliberately NOT in a style prop here, so React
    // has no record of it and will not reset it on re-render.
    <div className="landing-sky-page" ref={pageRef}>
      {/*
        The backdrop is position: fixed and clips the oversized disc. Fixed
        rather than absolute so (a) the gradient covers the viewport even when
        content scrolls on a short window, and (b) overscroll reveals sky, not
        page background. Decorative in full, so aria-hidden.
      */}
      <div className="landing-sky-backdrop" aria-hidden="true">
        <div className="landing-sky-glow" />

        <div className="landing-sky-disc" style={discStyle}>
          {/* ~1,900 faint points as the box-shadow of a single 1px element:
              one paint, one node, instead of a node per point. */}
          <div className="landing-sky-ambient" style={{ boxShadow: ambientBoxShadow }} />

          {stars.map((star) => (
            <div
              key={star.key}
              className={star.twinkles ? 'landing-sky-star landing-sky-twinkle' : 'landing-sky-star'}
              style={{
                left: star.left,
                top: star.top,
                width: star.size,
                height: star.size,
                opacity: star.opacity,
                background: star.color,
                boxShadow: star.glow,
                ...(star.twinkles
                  ? { animationDuration: star.twinkleDuration, animationDelay: star.twinkleDelay }
                  : null)
              }}
            />
          ))}
        </div>

        {/* Two decorative streaks. Positioned proportionally (1080/1280 and
            860/1280) so they land exactly as the reference has them at 1280 and
            stay in the same part of the sky at other widths. */}
        <div className="landing-sky-streak" style={{ left: '84.375%', top: '70px' }} />
        <div
          className="landing-sky-streak"
          style={{ left: '67.1875%', top: '30px', animationDelay: '5.5s' }}
        />

        {/* The ranges stretch to the viewport width; the conifers do not, and so
            are a separate SVG anchored bottom-right. See ConiferStand.js. */}
        <MountainRange />
        <ConiferStand />

        {/* The landing page's saucer, last in the fixed decorative layer so it
            passes in front of the mountains and behind the nav and hero. Not
            mounted at all unless both the page asks for it and SHOW_UFO is on,
            so with either off the DOM is exactly as before. See SkyUfo.js. */}
        {ufo && SHOW_UFO && <SkyUfo />}
      </div>

      {/* The lockup always links to "/" — on the landing page that is a no-op
          reload, which is the conventional behaviour for a masthead. navRight is
          rendered as given; absent, the nav has nothing opposite the lockup and
          flex leaves it on the left, which is the intended empty state. */}
      <header className="landing-sky-nav">
        <Link to="/" className="landing-sky-brand" aria-label="Constellation KG home">
          <DarkLockup />
        </Link>
        {navRight}
      </header>

      {children}

      {/* The dark footer variant lives in the shared MarketingFooter so the
          copyright string and the legal links have one home. This slot owns only
          its placement on the page. */}
      <div className="landing-sky-footer-slot">
        <MarketingFooter variant="dark" />
      </div>
    </div>
  );
};

export default SkyPage;
